import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { useLang } from "@/i18n";

const CONSENT_KEY = "ih_consent";

export default function CookieBanner() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!localStorage.getItem(CONSENT_KEY)) {
      const timer = setTimeout(() => setVisible(true), 1800);
      return () => clearTimeout(timer);
    }
  }, []);

  const save = (analytics, marketing) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ necessary: true, analytics, marketing, date: new Date().toISOString() }));
    setVisible(false);
  };

  const categories = [
    { key: "necessary", label: t("ck.necessary"), desc: t("ck.necessary_d"), locked: true },
    { key: "analytics", label: t("ck.analytics"), desc: t("ck.analytics_d"), locked: false },
    { key: "marketing", label: t("ck.marketing"), desc: t("ck.marketing_d"), locked: false },
  ];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 inset-x-4 md:bottom-6 md:left-auto md:right-6 md:max-w-md z-[70]"
          data-testid="cookie-banner"
          role="dialog"
          aria-label={t("ck.title")}
        >
          <div className="bg-cream-light border border-warm-grey/50 rounded-3xl shadow-2xl shadow-main/20 p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-sage/25 flex items-center justify-center shrink-0">
                  <Cookie size={18} className="text-green-dark" />
                </span>
                <h3 className="font-serif text-xl text-main">{t("ck.title")}</h3>
              </div>
              <button onClick={() => save(false, false)} aria-label="Close" data-testid="cookie-close"
                className="text-brown/40 hover:text-terracotta transition-colors">
                <X size={18} />
              </button>
            </div>

            <p className="mt-3 text-xs md:text-sm text-brown/75 font-light leading-relaxed">{t("ck.text")}</p>

            {customizing && (
              <div className="mt-4 space-y-3" data-testid="cookie-categories">
                {categories.map((c) => (
                  <label key={c.key} className={`flex items-start gap-3 rounded-2xl border border-warm-grey/50 bg-cream px-4 py-3 ${c.locked ? "opacity-70" : "cursor-pointer"}`} data-testid={`cookie-cat-${c.key}`}>
                    <input
                      type="checkbox"
                      checked={c.locked ? true : prefs[c.key]}
                      disabled={c.locked}
                      onChange={(e) => setPrefs({ ...prefs, [c.key]: e.target.checked })}
                      className="w-4 h-4 mt-0.5 accent-[#C67A5C]"
                      data-testid={`cookie-toggle-${c.key}`}
                    />
                    <span>
                      <span className="block text-sm font-semibold text-main">
                        {c.label} {c.locked && <span className="text-xs font-normal text-walnut">· {t("ck.always_on")}</span>}
                      </span>
                      <span className="block text-xs text-brown/60 font-light mt-0.5">{c.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              <button onClick={() => save(true, true)} data-testid="cookie-accept-all"
                className="rounded-full bg-terracotta text-cream-light text-sm font-semibold px-5 py-2.5 hover:bg-terracotta/90 transition-colors">
                {t("ck.accept_all")}
              </button>
              <button onClick={() => save(false, false)} data-testid="cookie-reject"
                className="rounded-full border border-warm-grey/60 text-brown text-sm font-semibold px-5 py-2.5 hover:border-terracotta transition-colors">
                {t("ck.reject")}
              </button>
              {customizing ? (
                <button onClick={() => save(prefs.analytics, prefs.marketing)} data-testid="cookie-save"
                  className="rounded-full bg-main text-cream-light text-sm font-semibold px-5 py-2.5 hover:bg-terracotta transition-colors">
                  {t("ck.save")}
                </button>
              ) : (
                <button onClick={() => setCustomizing(true)} data-testid="cookie-customize"
                  className="rounded-full text-brown/70 text-sm font-semibold px-3 py-2.5 hover:text-terracotta transition-colors">
                  {t("ck.customize")}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
